import { PocIcon } from 'commons/components/components-wrapper/poc-icon.component';
import { PocListItem } from 'commons/components/components-wrapper/poc-list-item.component';
import { PopoverMenuComponent, TooltipedIconComponent } from 'commons/components';
import { TooltipedElementComponent } from 'commons/components/tooltiped-element';
import { useTranslation } from 'core/i18n/i18n';
import { useActiveDeactivateDeviceConfiguration } from 'modules/settings/pods/settings-devices/settings-devices-configuration/hooks/use-active-deactivate-device-configuration.hook';
import { useDeviceConfigurationNavigation } from 'modules/settings/pods/settings-devices/settings-devices-configuration/hooks/use-device-configuration-navigation.hook';
import { DeviceConfigurationInfo } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-configuration-list/settings-devices-configuration-list.model';
import { SettingsDeviceRowOptionsStyled } from './settings-device-row-options.style';

interface SettingsDeviceRowOptionsProps {
    configuration: DeviceConfigurationInfo;
}

export const SettingsDeviceRowOptions = ({ configuration }: SettingsDeviceRowOptionsProps) => {
    const { t } = useTranslation();
    const { handleDeviceConfigSelected, handleOnCopyFrom, handleSetAsDefaultClick } =
        useDeviceConfigurationNavigation();
    const { handleActivateDeactivate, hasDefaultConfig } = useActiveDeactivateDeviceConfiguration();

    const handleCopyFrom = () => configuration && handleOnCopyFrom(configuration);

    const handleOnEdit = (ev: React.MouseEvent) => {
        ev.stopPropagation();
        if (configuration) {
            handleDeviceConfigSelected(configuration);
        }
    };

    const handleSetAsDefault = () => configuration && handleSetAsDefaultClick(configuration);

    const handleActivateDeactivateDevice = () => configuration && handleActivateDeactivate([configuration]);
    return (
        <SettingsDeviceRowOptionsStyled>
            <TooltipedIconComponent
                dataTestId={`options-menu-${configuration.id}-edit`}
                tooltipText={t(`settings.devices.device-settings.device-config-list.action.edit`)}
                onClick={handleOnEdit}
                icon="write"
                {...{ value: 'show-edit-device-settings-modal' }}
            />
            <PopoverMenuComponent
                key={`row-options-menu-${configuration.id}`}
                anchor={`row-options-menu-${configuration.id}`}
            >
                {!configuration?.isByDefault && configuration.active && (
                    <PocListItem
                        data-testid={`options-menu-${configuration.id}-set-as-default-from`}
                        onClick={handleSetAsDefault}
                        {...{ value: 'show-set-as-default-device-settings-modal' }}
                    >
                        <PocIcon slot="prefix" name="circle_confirm" family="outlined" />
                        {t(`settings.devices.device-settings.device-config-list.action.setAsDefault`)}
                    </PocListItem>
                )}
                <PocListItem
                    data-testid={`options-menu-${configuration.id}-copy-from`}
                    onClick={handleCopyFrom}
                    {...{ value: 'show-copy-from-device-settings-modal' }}
                >
                    <PocIcon slot="prefix" name="copy" family="outlined" />
                    {t(`settings.devices.device-settings.device-config-list.action.copyFrom`)}
                </PocListItem>
                <TooltipedElementComponent
                    hideTooltip={!hasDefaultConfig([configuration])}
                    placement="bottom"
                    dataTestId={`options-menu-${configuration.id}-active-deactivate`}
                    tooltipText={t(
                        `settings.devices.device-settings.device-config-list.action.activate-deactivate.by-default-warning`
                    )}
                >
                    <PocListItem
                        onClick={handleActivateDeactivateDevice}
                        disabled={hasDefaultConfig([configuration])}
                        {...{ value: 'show-bulk-actions-device-settings-modal' }}
                    >
                        <PocIcon
                            slot="prefix"
                            name={configuration.active ? 'circle_remove' : 'iso_on_off'}
                            family="outlined"
                        />
                        {t(
                            `settings.devices.device-settings.device-config-list.action.${configuration.active ? 'deactivate' : 'activate'}`
                        )}
                    </PocListItem>
                </TooltipedElementComponent>
            </PopoverMenuComponent>
        </SettingsDeviceRowOptionsStyled>
    );
};
